import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AppShell } from './components'
import { useAuth, useIsPlatformAdmin } from '../hooks'
import { buildNavigationItems } from './dashboard-layout/navigation'
import { buildWorkspaceName, useFormatRole } from './dashboard-layout/use-format-role'
import { useLanguageSync, useOrgName } from './dashboard-layout/use-dashboard-data'
import { logger } from '../utils/logger'

export function DashboardLayout(): React.ReactElement {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, isAuthenticated } = useAuth()
  const isPlatformAdmin = useIsPlatformAdmin()
  const { t, i18n } = useTranslation('nav')
  const formatRole = useFormatRole()

  useLanguageSync({ settings: user?.settings ?? null, i18n })
  const orgName = useOrgName(isPlatformAdmin, user?.organisationId)

  const navigationItems = buildNavigationItems({
    pathname: location.pathname,
    role: user?.role,
    isPlatformAdmin,
    t,
  })

  const handleNavigate = (href: string): void => {
    void navigate(href)
  }
  // FR4 outer safety net: if logout() ever throws despite the inner swallow
  // in services/auth.ts, navigation must still happen and no Uncaught
  // promise rejection must escape. Tracked in PRP §10 FUT-2 for future
  // replacement with a structured error pattern.
  const handleLogout = async (): Promise<void> => {
    try {
      await logout()
    } catch (error) {
      logger.warn('DashboardLayout.handleLogout', 'logout threw despite swallow; navigating anyway', {
        error: String(error),
      })
    } finally {
      void navigate('/')
    }
  }

  const shellUser = user
    ? { name: user.displayName ?? user.username, email: user.email }
    : undefined

  const workspaceName = user
    ? buildWorkspaceName({ role: formatRole(user.role), isPlatformAdmin, orgName })
    : 'EvidenX'

  return (
    <AppShell
      navigationItems={navigationItems}
      user={shellUser}
      workspace={{ name: workspaceName }}
      isAuthenticated={isAuthenticated}
      onNavigate={handleNavigate}
      onLogout={(...args: Parameters<typeof handleLogout>) => {
        void handleLogout(...args)
      }}
    >
      <div className="p-6">
        <Outlet />
      </div>
    </AppShell>
  )
}
